import React, { useState } from 'react';
import EditNoteIcon from '@mui/icons-material/EditNote';
import StarRateRoundedIcon from '@mui/icons-material/StarRateRounded';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';

import EditModal from '../modals/EditModal';
import { calculateAge } from '../../../utils/calculateAge';
import InputField from '../../common/fields/InputField';
import DateInputField from '../../common/fields/DateInputField';
import PrimaryButton from '../../common/buttons/PrimaryButton';
import AppSnackbar from '../../common/popups/AppSnackbar';
import useProfileSection from '../../../hooks/useProfileSection';
import useAvatarUpload from '../../../hooks/useAvatarUpload';
import { rules } from '../../../validations/rules';

function ProfileHeader({ teacher, canEdit, onUpdate }) {
  const { isEditing, openModal, closeModal, save, errors, loading, snackbarProps } =
    useProfileSection(teacher.profileId, onUpdate);

  const {
    avatarFile,
    avatarPreview,
    avatarError,
    handleAvatarChange,
    reset: resetAvatar,
  } = useAvatarUpload(teacher.avatar);

  const [formData, setFormData] = useState({
    firstName: teacher.firstName || '',
    lastName: teacher.lastName || '',
    phone: teacher.phone || '',
    dob: teacher.dob || '',
  });
  const [localErrors, setLocalErrors] = useState({});
  const fieldErrors = { ...errors, ...localErrors };

  const age = teacher.dob ? calculateAge(teacher.dob) : null;

  const renderReviews = (rating) => {
    return [...Array(5)].map((_, i) => {
      const full = i < Math.floor(rating);
      const half = !full && i < rating;

      return (
        <span key={i} className="relative inline-block">
          <StarRateRoundedIcon className="text-gray-400/50" />
          {(full || half) && (
            <span
              className="absolute inset-0 overflow-hidden"
              style={{ width: full ? '100%' : '50%' }}
            >
              <StarRateRoundedIcon className="text-primary absolute inset-0" />
            </span>
          )}
        </span>
      );
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (avatarError) return;

    const phoneError = rules.phone(formData.phone);
    if (phoneError) {
      setLocalErrors({ phone: phoneError });
      return;
    }

    setLocalErrors({});

    const payload = new FormData();
    payload.append('_method', 'PUT');
    payload.append('firstName', formData.firstName);
    payload.append('lastName', formData.lastName);
    payload.append('phone', formData.phone);
    if (formData.dob) payload.append('dob', formData.dob);
    if (avatarFile) payload.append('avatar', avatarFile);

    await save(payload, { successMessage: 'Profile updated!' });
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 via-gray-900 to-orange-900/20 py-12 rounded-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-end w-full">
          {canEdit && (
            <button onClick={openModal} className="text-primary hover:text-orange-400 transition">
              <EditNoteIcon fontSize="large" />
            </button>
          )}
        </div>

        <div className="text-center relative">
          <div className="relative inline-block">
            <img
              src={teacher.avatar ?? '/assets/images/profile_placeholder.jpg'}
              alt={`${teacher.firstName} ${teacher.lastName}`}
              className="relative w-64 h-64 rounded-full object-cover border-8 border-gray-900 mx-auto lg:mx-0"
            />
          </div>

          <div className="mt-6 flex items-center justify-center gap-3 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              {teacher.firstName} {teacher.lastName}
            </h1>
          </div>

          {teacher.code ? (
            <div className="mt-3 flex justify-center">
              <span className="rounded-full border border-primary/40 bg-primary/15 px-4 py-1 text-sm font-semibold text-primary shadow-sm shadow-primary/10">
                ID: {teacher.code}
              </span>
            </div>
          ) : null}

          {age && (
            <div className="mt-4">
              <div className="flex items-center text-gray-400 justify-center">
                <PersonOutlineRoundedIcon />
                <span className="ms-2">{age} Years old</span>
              </div>
            </div>
          )}

          <hr className="mt-5 mb-8 border-white/20 w-3/4 mx-auto" />

          <div className="flex items-center justify-center mt-4">
            <div className="flex">{renderReviews(teacher.rating)}</div>
            <span className="text-white font-semibold ml-2">
              {teacher.rating > 0 ? teacher.rating.toFixed(1) : '—'}
            </span>
            <span className="text-gray-400 ml-1">
              ({teacher.reviews?.length} {teacher.reviews?.length === 1 ? 'Review' : 'Reviews'})
            </span>
          </div>
        </div>
      </div>

      <EditModal
        isOpen={isEditing}
        onClose={() => {
          closeModal();
          resetAvatar();
          setLocalErrors({});
        }}
        title="Edit Profile"
      >
        <form onSubmit={handleSave}>
          <div className="space-y-6 mb-10">
            {/* Avatar */}
            <div className="flex flex-col items-center">
              <div className="relative form-group">
                <img
                  src={avatarPreview ?? '/assets/images/profile_placeholder.jpg'}
                  alt="Profile"
                  className="w-32 h-32 rounded-full object-cover border-4 border-gray-700"
                />
                <label
                  htmlFor="avatar-upload-header"
                  className="absolute w-[35px] h-[35px] flex items-center justify-center bottom-0 right-0 bg-orange-500 hover:bg-orange-600 text-white p-2 rounded-full cursor-pointer transition"
                >
                  <CameraAltOutlinedIcon sx={{ fontSize: 18 }} />
                </label>
                <input
                  id="avatar-upload-header"
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="hidden"
                />
              </div>
              {avatarError && <p className="text-red-400 text-sm mt-2">{avatarError}</p>}
              {errors.avatar && <p className="text-red-400 text-sm mt-2">{errors.avatar}</p>}
            </div>

            {/* Name fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <InputField
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                placeholder="Enter first name"
                error={!!fieldErrors.firstName}
                helperText={fieldErrors.firstName}
              />
              <InputField
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                placeholder="Enter last name"
                error={!!fieldErrors.lastName}
                helperText={fieldErrors.lastName}
              />
            </div>

            {/* Phone */}
            <InputField
              label="Phone Number"
              type="tel"
              value={formData.phone}
              onChange={(e) => {
                setFormData({ ...formData, phone: e.target.value });
                if (localErrors.phone) {
                  const { phone: _, ...rest } = localErrors;
                  setLocalErrors(rest);
                }
              }}
              placeholder="Enter phone number"
              error={!!fieldErrors.phone}
              helperText={fieldErrors.phone}
              inputProps={{
                inputMode: 'numeric',
                maxLength: 10,
                pattern: '0[0-9]{9}',
              }}
            />

            {/* Date of birth */}
            <DateInputField
              label="Date of Birth"
              value={formData.dob}
              onChange={(date) => setFormData({ ...formData, dob: date })}
              disableFuture
              required
              error={!!fieldErrors.dob}
              helperText={fieldErrors.dob}
            />
          </div>

          <div className="flex justify-end">
            <PrimaryButton
              className="w-full sm:w-auto"
              type="submit"
              disabled={loading || !!avatarError}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </PrimaryButton>
          </div>
        </form>
      </EditModal>

      <AppSnackbar {...snackbarProps} />
    </div>
  );
}

export default ProfileHeader;
